import { useState, useEffect, useRef } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const EMPTY = { name: '', email: '', web: '', message: '' };

export default function AnalysisModal({ isOpen, onClose }: Props) {
  const [form, setForm] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);

  // ESC key + body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => firstRef.current?.focus(), 60);
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) { setForm(EMPTY); setSubmitted(false); }
  }, [isOpen]);

  if (!isOpen) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(onClose, 3000);
  }

  function set(field: keyof typeof EMPTY) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  return (
    <div
      className="modal-overlay"
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-card">
        <button className="modal-close" onClick={onClose} aria-label="Zavrieť">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {submitted ? (
          <div className="modal-success">
            <div className="modal-success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 className="modal-success-title">Ďakujeme!</h3>
            <p className="modal-success-sub">Vaša správa bola odoslaná. Ozveme sa do 24 hodín.</p>
          </div>
        ) : (
          <>
            <h2 id="modal-title" className="modal-title">Chcem bezplatnú analýzu</h2>
            <p className="modal-sub">
              Vyplňte formulár a my sa vám ozveme do 24 hodín s konkrétnymi odporúčaniami.
            </p>
            <form onSubmit={handleSubmit} className="modal-form" noValidate>
              <div className="modal-row">
                <div className="modal-field">
                  <label htmlFor="m-name">Meno *</label>
                  <input
                    ref={firstRef}
                    id="m-name" type="text" placeholder="Vaše meno"
                    required value={form.name} onChange={set('name')}
                  />
                </div>
                <div className="modal-field">
                  <label htmlFor="m-email">Email *</label>
                  <input
                    id="m-email" type="email" placeholder="vas@email.sk"
                    required value={form.email} onChange={set('email')}
                  />
                </div>
              </div>
              <div className="modal-field">
                <label htmlFor="m-web">Web (URL)</label>
                <input
                  id="m-web" type="url" placeholder="https://vashweb.sk"
                  value={form.web} onChange={set('web')}
                />
              </div>
              <div className="modal-field">
                <label htmlFor="m-msg">Správa</label>
                <textarea
                  id="m-msg" placeholder="Čo by ste chceli zlepšiť?" rows={3}
                  value={form.message} onChange={set('message')}
                />
              </div>
              <button type="submit" className="btn-primary modal-submit">
                Odoslať →
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
